import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import Techstack from "./Techstack";
import Aboutcard from "./AboutCard";
import laptopImg from "../../Assets/about.png";
import Toolstack from "./Toolstack";
import {
  FaBullseye,
  FaComments,
  FaGlobe,
  FaLayerGroup,
  FaPlug,
  FaShieldAlt,
} from "react-icons/fa";
import usePageMeta from "../../hooks/usePageMeta";

function About() {
  usePageMeta(
    "About | Om Jaiswal",
    "About Om Jaiswal, a full stack developer building websites, web apps, and APIs with clear UX, solid architecture, and dependable delivery."
  );

  const trustSignals = [
    {
      Icon: FaBullseye,
      title: "Product thinking",
      description: "I reduce ambiguity before it gets expensive.",
    },
    {
      Icon: FaShieldAlt,
      title: "Trust-heavy domains",
      description: "Strong fit for healthcare, operations, and client-facing products.",
    },
    {
      Icon: FaLayerGroup,
      title: "Maintainable systems",
      description: "Clean enough for teams to extend without rework.",
    },
    {
      Icon: FaComments,
      title: "Clear delivery",
      description: "Tradeoffs and progress stay visible throughout the work.",
    },
  ];

  const bestFitAreas = [
    {
      Icon: FaGlobe,
      label: "Website work",
      title: "Credibility-first websites",
      description: "Sharper structure, cleaner messaging, stronger first impression.",
      signals: ["Structure", "Messaging", "Responsive"],
      accent: "#60d5ff",
      accentSoft: "rgba(96, 213, 255, 0.12)",
    },
    {
      Icon: FaLayerGroup,
      label: "Product UI",
      title: "Portals and dashboards",
      description: "Operational screens and client flows that feel calmer fast.",
      signals: ["Flows", "States", "Dashboards"],
      accent: "#a78bfa",
      accentSoft: "rgba(167, 139, 250, 0.14)",
    },
    {
      Icon: FaPlug,
      label: "Connected flows",
      title: "Products with integration",
      description: "Useful connections wrapped in clear product behavior.",
      signals: ["APIs", "Handoffs", "Validation"],
      accent: "#34d399",
      accentSoft: "rgba(52, 211, 153, 0.14)",
    },
  ];

  return (
    <Container fluid className="about-section">
      <Particle />
      <Container className="section-shell">
        <Row className="align-items-center about-intro-row">
          <Col
            lg={7}
            className="about-intro-copy-col"
          >
            <p className="section-kicker">About</p>
            <h1 className="section-title page-hero-title">
              I build clear websites, web apps, and the systems behind them.
            </h1>
            <Aboutcard />
          </Col>
          <Col
            lg={5}
            className="about-img about-intro-visual-col"
          >
            <img
              src={laptopImg}
              alt="Illustration of Om Jaiswal working on product and engineering planning"
              className="img-fluid about-laptop-img"
            />
          </Col>
        </Row>

        <div className="section-spacer">
          <div className="section-header section-header-left">
            <p className="section-kicker">Why clients trust the work</p>
            <h2 className="section-title section-title-compact">
              Reliable delivery over noise.
            </h2>
          </div>

          <div className="value-grid">
            {trustSignals.map((item) => (
              <article key={item.title} className="info-card info-card-compact">
                <span className="info-card-icon" aria-hidden="true">
                  <item.Icon />
                </span>
                <h3 className="info-card-title">{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="section-spacer">
          <div className="section-header section-header-left">
            <p className="section-kicker">Best fit</p>
            <h2 className="section-title section-title-compact">
              Where I am most useful.
            </h2>
          </div>

          <Row className="card-grid-row g-4">
            {bestFitAreas.map((item) => (
              <Col md={6} lg={4} key={item.title}>
                <article
                  className="service-card service-card-featured"
                  style={{
                    "--service-accent": item.accent,
                    "--service-accent-soft": item.accentSoft,
                  }}
                >
                  <div className="service-card-top">
                    <span className="service-card-kicker">{item.label}</span>
                    <div className="service-card-icon" aria-hidden="true">
                      <item.Icon />
                    </div>
                  </div>
                  <h3 className="service-card-title">{item.title}</h3>
                  <p className="service-card-summary">{item.description}</p>
                  <div className="service-card-signal-row">
                    {item.signals.map((signal) => (
                      <span key={signal} className="service-card-signal">
                        {signal}
                      </span>
                    ))}
                  </div>
                </article>
              </Col>
            ))}
          </Row>
        </div>

        <div className="section-spacer">
          <div className="section-header section-header-left">
            <p className="section-kicker">Technical context</p>
            <h2 className="section-title section-title-compact">
              Core stack.
            </h2>
          </div>

          <Techstack />
        </div>

        <div className="section-spacer">
          <div className="section-header section-header-left">
            <p className="section-kicker">Delivery environment</p>
            <h2 className="section-title section-title-compact">
              Build and release tools.
            </h2>
          </div>

          <Toolstack />
        </div>
      </Container>
    </Container>
  );
}

export default About;
